/* URNSMITH theme — small progressive enhancements.
   Every feature works without this file: links, native forms and <details> carry the basics. */
(() => {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  /* ---------- Header: mobile menu ---------- */
  const menuToggle = $('[data-menu-toggle]');
  const mobileNav = $('#MobileNav');
  if (menuToggle && mobileNav) {
    const setMenu = (open) => {
      menuToggle.setAttribute('aria-expanded', String(open));
      mobileNav.hidden = !open;
      if (open) {
        const firstLink = $('a', mobileNav);
        if (firstLink) firstLink.focus();
      }
    };
    menuToggle.addEventListener('click', () => setMenu(menuToggle.getAttribute('aria-expanded') !== 'true'));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !mobileNav.hidden) {
        setMenu(false);
        menuToggle.focus();
      }
    });
    window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
      if (event.matches) setMenu(false);
    });
  }

  /* ---------- Header: search panel ---------- */
  const searchToggle = $('[data-search-toggle]');
  const searchPanel = $('#HeaderSearch');
  if (searchToggle && searchPanel) {
    const setSearch = (open) => {
      searchToggle.setAttribute('aria-expanded', String(open));
      searchPanel.hidden = !open;
      if (open) $('input[type="search"]', searchPanel).focus();
    };
    searchToggle.addEventListener('click', (event) => {
      event.preventDefault();
      setSearch(searchPanel.hidden);
    });
    const closeButton = $('[data-search-close]', searchPanel);
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        setSearch(false);
        searchToggle.focus();
      });
    }
    searchPanel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setSearch(false);
        searchToggle.focus();
      }
    });
  }

  /* ---------- Quantity steppers ---------- */
  $$('[data-quantity]').forEach((wrapper) => {
    const input = $('input', wrapper);
    const minus = $('[data-quantity-minus]', wrapper);
    const plus = $('[data-quantity-plus]', wrapper);
    if (!input || !minus || !plus) return;
    const min = () => Number(input.min || 0);
    const sync = () => {
      minus.disabled = (parseInt(input.value, 10) || 0) <= min();
    };
    const step = (delta) => {
      const value = Math.max(min(), (parseInt(input.value, 10) || 0) + delta);
      input.value = String(value);
      sync();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    minus.addEventListener('click', () => step(-1));
    plus.addEventListener('click', () => step(1));
    input.addEventListener('input', sync);
    sync();
  });

  /* ---------- Cart: update quantities without a separate button ---------- */
  const cartForm = $('[data-cart-form]');
  if (cartForm) {
    let timer;
    cartForm.addEventListener('change', (event) => {
      if (!event.target.matches('[data-cart-quantity]')) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        cartForm.setAttribute('aria-busy', 'true');
        const update = cartForm.querySelector('button[name="update"]');
        if (cartForm.requestSubmit && update) cartForm.requestSubmit(update);
        else cartForm.submit();
      }, 450);
    });
  }

  /* ---------- Collection: filters and sorting ---------- */
  const submitFilters = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    const query = params.toString();
    window.location.assign(form.getAttribute('action') + (query ? `?${query}` : ''));
  };
  $$('[data-facets-form]').forEach((form) => {
    let timer;
    form.addEventListener('change', (event) => {
      clearTimeout(timer);
      const delay = event.target.matches('[data-price-input]') ? 600 : 150;
      timer = setTimeout(() => submitFilters(form), delay);
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitFilters(form);
    });
  });
  $$('[data-auto-submit]').forEach((select) => {
    select.addEventListener('change', () => {
      if (select.form) submitFilters(select.form);
    });
  });
  $$('[data-filter-toggle]').forEach((button) => {
    const target = document.getElementById(button.getAttribute('aria-controls'));
    const panel = target ? target.closest('.facets') : null;
    if (!panel) return;
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      panel.classList.toggle('is-open', open);
    });
  });

  /* ---------- Product gallery ---------- */
  const initGallery = (gallery) => {
    const viewport = $('[data-gallery-viewport]', gallery);
    if (!viewport) return () => {};
    const slides = $$('.gallery__slide', viewport);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const prev = $('[data-gallery-prev]', gallery);
    const next = $('[data-gallery-next]', gallery);
    const counter = $('[data-gallery-current]', gallery);

    const setActive = (index) => {
      if (counter) counter.textContent = String(index + 1);
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    };
    const currentIndex = () => Math.round(viewport.scrollLeft / Math.max(viewport.clientWidth, 1));
    const goTo = (index) => {
      const target = Math.max(0, Math.min(slides.length - 1, index));
      viewport.scrollTo({ left: slides[target].offsetLeft, behavior: 'smooth' });
      setActive(target);
    };

    let frame;
    viewport.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setActive(currentIndex()));
    }, { passive: true });
    if (prev) prev.addEventListener('click', () => goTo(currentIndex() - 1));
    if (next) next.addEventListener('click', () => goTo(currentIndex() + 1));
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
    setActive(0);

    return (mediaId) => {
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) goTo(index);
    };
  };

  /* ---------- Product variants ---------- */
  $$('[data-product]').forEach((root) => {
    const gallery = $('[data-gallery]', root);
    const goToMedia = gallery ? initGallery(gallery) : () => {};

    const data = document.querySelector(`[data-product-variants="${root.dataset.sectionId}"]`);
    const picker = $('[data-variant-picker]', root);
    const select = $('[data-variant-select]', root);
    if (!data || !picker || !select) return;

    const variants = JSON.parse(data.textContent);
    const addButton = $('[data-add-button]', root);
    const addLabel = $('[data-add-label]', root);
    const priceValue = $('[data-price-value]', root);
    const priceCompare = $('[data-price-compare]', root);
    const status = $('[data-variant-status]', root);
    const labels = {
      add: root.dataset.labelAdd,
      soldOut: root.dataset.labelSoldOut,
      unavailable: root.dataset.labelUnavailable,
    };
    const groups = $$('fieldset', picker);
    const matches = (options) => variants.find((variant) => variant.options.every((value, i) => value === options[i]));

    const update = () => {
      const chosen = groups.map((group) => {
        const checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
      const variant = matches(chosen);

      groups.forEach((group, i) => {
        const shown = $('[data-option-selected]', group);
        if (shown) shown.textContent = chosen[i] || '';
        $$('input', group).forEach((input) => {
          const test = chosen.slice();
          test[i] = input.value;
          const candidate = matches(test);
          const unavailable = !candidate || !candidate.available;
          input.closest('.option-value').classList.toggle('is-unavailable', unavailable);
          const note = $('[data-unavailable-text]', input.closest('.option-value'));
          if (note) note.textContent = unavailable ? `, ${labels.unavailable}` : '';
        });
      });

      if (!variant) {
        addButton.disabled = true;
        addLabel.textContent = labels.unavailable;
        status.textContent = labels.unavailable;
        return;
      }

      select.value = String(variant.id);
      priceValue.textContent = variant.price;
      priceCompare.textContent = variant.compare || '';
      priceCompare.hidden = !variant.compare;
      addButton.disabled = !variant.available;
      addLabel.textContent = variant.available ? labels.add : labels.soldOut;
      status.textContent = `${chosen.join(' / ')}: ${variant.price}${variant.available ? '' : `, ${labels.soldOut}`}`;
      if (variant.media) goToMedia(variant.media);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    picker.addEventListener('change', update);

    // A link to a specific variant opens the gallery on that variant's image.
    const linked = new URL(window.location.href).searchParams.get('variant');
    const initial = linked && variants.find((variant) => String(variant.id) === linked);
    if (initial && initial.media) {
      const viewport = $('[data-gallery-viewport]', root);
      const slide = viewport && viewport.querySelector(`[data-media-id="${initial.media}"]`);
      if (slide) {
        viewport.style.scrollBehavior = 'auto';
        viewport.scrollLeft = slide.offsetLeft;
        viewport.style.scrollBehavior = '';
      }
    }
  });
})();
